import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { manufacturersApi, type ManufacturerView } from "../../api/assets";
import type { ApiError } from "../../api/client";
import { detailsMap, formatApiError } from "../../lib/apiError";
import { matchesQuery } from "../../lib/foldText";
import { useManufacturers } from "../../lib/useManufacturers";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import { Modal } from "../../components/Modal";
import { TextPopover } from "../../components/TextPopover";
import Ic from "../../components/icons.jsx";

type FormMode = { kind: "create" } | { kind: "edit"; row: ManufacturerView };

/**
 * Manufacturer codebook ("Proizvođači", sprint 5g): the list the asset form
 * picks the maker of a piece from, each with an optional note (contact,
 * service…). A rename here shows on every asset at once;
 * a manufacturer still set on an asset cannot be deleted. The table reads the
 * shared `useManufacturers` cache and every write refreshes it, so an asset
 * form open elsewhere in the SPA sees the change.
 */
export default function AdminManufacturersPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("manufacturers:create");
  const canEdit = hasPermission("manufacturers:edit");
  const canDelete = hasPermission("manufacturers:delete");
  const hasActions = canEdit || canDelete;
  const { manufacturers, loading, error, refresh } = useManufacturers();
  const [q, setQ] = useState("");
  const [form, setForm] = useState<FormMode | null>(null);

  const visible = useMemo(() => manufacturers.filter((m) => matchesQuery(`${m.name} ${m.note ?? ""}`, q)), [manufacturers, q]);

  async function handleDelete(row: ManufacturerView) {
    const ok = await confirm({
      title: t("manufacturers.confirmDelete.title", "Brisanje proizvođača"),
      message: t("manufacturers.confirmDelete.message", "Obrisati proizvođača „{{name}}“ iz šifarnika?", { name: row.name }),
      confirmLabel: t("action.delete", "Obriši"),
      danger: true,
    });
    if (!ok) return;
    try {
      await manufacturersApi.remove(row.id);
      toast.success(t("manufacturers.toast.deleted", "Proizvođač obrisan."));
      await refresh();
    } catch (err) {
      const e = err as ApiError;
      if (e.code === "IN_USE") {
        const count = Number(detailsMap(e)?.assetCount ?? row.assetCount);
        toast.error(t("manufacturers.error.inUse", "Proizvođač je upisan na {{count}} asseta — prvo ga promenite na tim assetima.", { count }));
      } else {
        toast.error(formatApiError(e));
      }
    }
  }

  async function handleSaved(created: boolean) {
    setForm(null);
    toast.success(created ? t("manufacturers.toast.created", "Proizvođač dodat.") : t("manufacturers.toast.saved", "Proizvođač sačuvan."));
    await refresh();
  }

  const colName = t("manufacturers.col.name", "Naziv");
  const colNote = t("manufacturers.col.note", "Napomena");
  const colAssets = t("manufacturers.col.assetCount", "Asseta");
  const colActions = t("common.actions", "Radnje");

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t("manufacturers.title", "Proizvođači")}</h1>
          <div className="sub">{t("manufacturers.subtitle", "Šifarnik proizvođača — Sever, SEW, Siemens… Na assetu se proizvođač bira iz ove liste, ne kuca se.")}</div>
        </div>
        {canCreate && (
          <div className="page-actions">
            <button type="button" className="btn primary" onClick={() => setForm({ kind: "create" })}>
              <Ic.plus /> {t("manufacturers.new", "Novi proizvođač")}
            </button>
          </div>
        )}
      </div>

      <div className="filter-bar">
        <div className="search-wrap">
          <Ic.search aria-hidden="true" />
          <input
            className="input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("manufacturers.filter.searchPlaceholder", "Pretraga po nazivu ili napomeni…")}
            aria-label={t("action.search", "Pretraga")}
          />
        </div>
        {q !== "" && (
          <button type="button" className="btn sm ghost" onClick={() => setQ("")}>
            {t("action.clear", "Očisti")}
          </button>
        )}
      </div>

      {error && <div style={errorBox} role="alert">{error}</div>}

      <section className="card">
        {loading ? (
          <div className="empty">{t("common.loading", "Učitavanje…")}</div>
        ) : visible.length === 0 ? (
          <div className="empty">
            {manufacturers.length === 0
              ? t("manufacturers.empty", "Šifarnik je prazan. Dodajte prvog proizvođača.")
              : t("manufacturers.emptyFiltered", "Nijedan proizvođač ne odgovara pretrazi.")}
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl list-card">
              <thead>
                <tr>
                  <th>{colName}</th>
                  <th>{colNote}</th>
                  <th className="num">{colAssets}</th>
                  {hasActions && <th style={{ textAlign: "right" }}>{colActions}</th>}
                </tr>
              </thead>
              <tbody>
                {visible.map((m) => (
                  <tr key={m.id}>
                    <td data-label={colName}><b>{m.name}</b></td>
                    <td className="cell-note" data-label={colNote}>{m.note ? <TextPopover text={m.note} /> : <span className="muted">—</span>}</td>
                    <td className="num" data-label={colAssets}>{m.assetCount > 0 ? m.assetCount : <span className="muted">—</span>}</td>
                    {hasActions && (
                      <td className="card-actions" style={{ textAlign: "right", whiteSpace: "nowrap" }} data-label={colActions}>
                        {canEdit && (
                          <button type="button" className="btn sm" onClick={() => setForm({ kind: "edit", row: m })} aria-label={t("manufacturers.editAria", "Izmeni proizvođača: {{name}}", { name: m.name })}>
                            {t("action.edit", "Izmeni")}
                          </button>
                        )}
                        {canDelete && (
                          <button type="button" className="btn sm" style={{ ...dangerBtn, marginLeft: 6 }} onClick={() => handleDelete(m)} aria-label={t("manufacturers.deleteAria", "Obriši proizvođača: {{name}}", { name: m.name })}>
                            {t("action.delete", "Obriši")}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {form && <ManufacturerFormModal mode={form} onClose={() => setForm(null)} onSaved={handleSaved} />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ManufacturerFormModal({ mode, onClose, onSaved }: {
  mode: FormMode;
  onClose: () => void;
  onSaved: (created: boolean) => Promise<void>;
}) {
  const { t } = useTranslation();
  const editing = mode.kind === "edit" ? mode.row : null;
  const [name, setName] = useState(editing?.name ?? "");
  const [note, setNote] = useState(editing?.note ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
  }, [name, note]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const body = { name: trimmed, note: note.trim() || null };
      if (editing) await manufacturersApi.update(editing.id, body);
      else await manufacturersApi.create(body);
      await onSaved(editing === null);
    } catch (err) {
      const ae = err as ApiError;
      setError(ae.code === "NAME_TAKEN"
        ? t("manufacturers.error.nameTaken", "Proizvođač „{{name}}“ već postoji.", { name: trimmed })
        : formatApiError(ae));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={editing ? t("manufacturers.modal.editTitle", "Izmena proizvođača") : t("manufacturers.modal.createTitle", "Novi proizvođač")}
      onClose={onClose}
      width={420}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {editing && editing.assetCount > 0 && (
          <div className="muted" style={{ fontSize: 13 }} role="note">
            {t("manufacturers.modal.inUseNote", "Upisan je na {{count}} asseta — novi naziv važi za sve njih.", { count: editing.assetCount })}
          </div>
        )}
        <label className="field">
          <span>{t("manufacturers.field.name", "Naziv")} <span className="req-mark">*</span></span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} placeholder={t("manufacturers.field.namePlaceholder", "npr. Sever")} />
        </label>
        <label className="field">
          <span>{t("manufacturers.field.note", "Napomena")}</span>
          <textarea
            className="input"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
            placeholder={t("manufacturers.field.notePlaceholder", "npr. kontakt, ovlašćeni servis, rok isporuke…")}
            style={textarea}
          />
        </label>
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary" disabled={submitting || !name.trim()}>
            {submitting ? t("action.saving", "Čuvanje…") : t("action.save", "Sačuvaj")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13, marginBottom: 12 };
const textarea: CSSProperties = { resize: "vertical", fontFamily: "inherit", width: "100%" };
const dangerBtn: CSSProperties = { color: "var(--err)", borderColor: "rgba(182,53,58,.35)" };
